import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../context/UserContext';
import logo from '../../icons/logo.svg';
import '../../style/ConfirmEmail.css';

const ENVIRONMENT = process.env.NODE_ENV || 'development';
const SERVER_BASE_URL = ENVIRONMENT === 'development' ? 'http://localhost:3001' : '';

const ConfirmEmail: React.FC = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const email = location.state?.email;
    const from = location.state?.from || "/home";

    const [confirmationCode, setConfirmationCode] = useState(Array(6).fill(''));
    const [error, setError] = useState('');

    const handleConfirm = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const code = confirmationCode.join('');
        
        try {
            const response = await fetch(`${SERVER_BASE_URL}/auth/confirm-login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, confirmationCode: code }),
                credentials: 'include'
            });

            if (response.ok) {
                window.location.href = from;
            } else {
                const errorData = await response.json();
                setError(errorData.message || t('confirmEmail.invalidCode'));
            }
        } catch (error) {
            console.error('Error confirming email:', error);
            setError(t('confirmEmail.unexpectedError'));
        }
    };

    const handleChange = (index: number, value: string) => {
        if (/^[0-9]?$/.test(value)) { // Allow only digits
            const updatedCode = [...confirmationCode];
            updatedCode[index] = value;
            setConfirmationCode(updatedCode);

            // Move focus to the next input if value is entered
            if (value && index < 5) {
                const nextInput = document.getElementById(`code-${index + 1}`);
                nextInput?.focus();
            }
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        const pasteData = e.clipboardData.getData('Text').slice(0, 6); // Take only first 6 characters
        if (/^\d{6}$/.test(pasteData)) { // Validate it’s a 6-digit number
            setConfirmationCode(pasteData.split(''));
        }
    };

    return (
        <div className="confirm-email-container">
            <div className="confirm-email-form">
                <img src={logo} alt="app Logo" className="confirm-logo" />
                <h1 className='confirm-title'>{t('confirmEmail.title')}</h1>
                <p className='confirm-subtitle'>{t('confirmEmail.subtitle1', {email: email})}</p>
                <p className='confirm-subtitle'>{t('confirmEmail.subtitle2')}</p>
                <form onSubmit={handleConfirm}>
                    <div className="code-inputs">
                        {confirmationCode.map((digit, index) => (
                            <input
                                key={index}
                                id={`code-${index}`}
                                type="text"
                                inputMode="numeric"
                                maxLength={1}
                                value={digit}
                                onChange={(e) => handleChange(index, e.target.value)}
                                onPaste={handlePaste}
                                className="code-input"
                                required
                            />
                        ))}
                    </div>
                    {error && <p className="error-message">{error}</p>}
                    <button type="submit" className="confirm-button">
                        {t('confirmEmail.confirmButton')}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ConfirmEmail;
